import { cookieManager } from './chunk-BB4HZKHH.js';
import { config } from './chunk-FNDFUF2Y.js';

// ../base/auth/error.ts
var ERROR_CODES = ["MISSING_TOKEN", "MFA_REQUIRED"];
var JwAuthError = class extends Error {
  static CODES = ERROR_CODES;
  name = "JwAuthError";
  code;
  constructor(code) {
    super(code);
    this.code = code;
  }
  toString() {
    return this.code;
  }
};
function isJwAuthError(e) {
  return e instanceof JwAuthError;
}

// ../base/util/environment.ts
function getEnvVariable(variable) {
  if (typeof window !== "undefined") {
    return import.meta.env[variable];
  } else if (typeof process !== "undefined" && process.versions != null && process.versions.node != null) {
    return process.env[variable];
  }
  throw new Error("Unsupported environment");
}

// ../../node_modules/@jwplayer/fe-common/module/util/promise/index.js
var PromiseUtil = {
  /**
   * Returns a cached promise while it is pending
   *
   * Once the promise is resolved, the cache is cleared and subsequent calls will create a new promise.
   */
  coalesce(fn) {
    let promise;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args).finally(() => {
        promise = void 0;
      });
      return promise;
    };
  },
  /**
   * Returns a cached promise, ensuring the promise runs only once
   *
   * Unlike memoization, this returns the promise, not the result.
   */
  once(fn) {
    let promise = void 0;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args);
      return promise;
    };
  }
};

// ../../node_modules/@jwplayer/fe-common/module/inline-worker/index.js
function createInlineWorker(fn, __TEST_CONTEXT__ = false) {
  if (__TEST_CONTEXT__) {
    const handlers = {
      toWorker: [],
      toBrowser: []
    };
    fn({
      postMessage: (data) => {
        handlers.toBrowser.forEach((fn2) => fn2(new MessageEvent("message", { data })));
      },
      addEventListener(_, handler) {
        handlers.toWorker.push(handler);
      }
    });
    return {
      postMessage: (data) => {
        handlers.toWorker.forEach((fn2) => fn2(new MessageEvent("message", { data })));
      },
      addEventListener(_, handler) {
        handlers.toBrowser.push(handler);
      }
    };
  }
  const blob = new Blob([`const fn = ${fn.toString()}; fn(globalThis);`], { type: "text/javascript" });
  const worker = new Worker(URL.createObjectURL(blob));
  return worker;
}

// ../base/auth/worker/index.ts
var getWorker = () => {
  return createInlineWorker(
    function(ctx) {
      let timeout;
      ctx.addEventListener("message", ({ data: [eventName, eventArg] }) => {
        switch (eventName) {
          case "start": {
            clearTimeout(timeout);
            timeout = setTimeout(async () => {
              ctx.postMessage(["ping"]);
            }, eventArg);
            break;
          }
          case "stop": {
            clearTimeout(timeout);
            break;
          }
        }
      });
    },
    Boolean(import.meta.env.TEST)
  );
};

// ../base/auth/manager.ts
var AuthManager = class {
  eventHandlers = {
    authenticated: [],
    refreshFailed: [],
    expired: [],
    cleared: []
  };
  /**
   * Cached 'session' object
   */
  cache;
  lastRefresh;
  opts;
  /**
   * Cached token
   */
  cachedToken;
  worker = getWorker();
  constructor(opts) {
    this.opts = {
      authTokenGracePeriod: 10 * 1e3,
      ...opts
    };
    this.worker.addEventListener("message", async ({ data: [eventName] }) => {
      if (String(eventName) !== String("ping")) {
        return;
      }
      try {
        const { obj, token } = await this.authenticateApi({});
        this.setCache(obj, token);
      } catch (e) {
        this.clear();
        this.emit("refreshFailed", e);
      }
    });
  }
  stopRefreshTimers() {
    this.worker.postMessage(["stop"]);
  }
  /**
   * Clear the cached session and auth token (cookie).
   */
  clear() {
    this.cachedToken = void 0;
    cookieManager.deleteCookie(this.opts.authCookieName());
    this.cache = void 0;
    this.stopRefreshTimers();
    this.emit("cleared");
  }
  /**
   * Emit event (with arguments)
   */
  emit(event, ...args) {
    this.eventHandlers[event].forEach((h) => h.apply(h, args));
  }
  /**
   * API call needs to be implemented in child class
   */
  async authenticateApi(_opts) {
    throw new Error("Not implemented");
  }
  /**
   * API call needs to be implemented in child class
   */
  async loginApi(_params, _opts) {
    throw new Error("Not implemented");
  }
  /**
   * Returns an existing session if there is one. If not, then an API
   * call is made to refresh the session from an auth cookie.
   */
  async authenticate(opts) {
    const cache = this.getCache();
    if (cache) {
      return cache;
    }
    const token = this.getToken();
    if (!token) {
      throw new JwAuthError("MISSING_TOKEN");
    }
    try {
      const res = await this.authenticateApi(opts);
      this.setCache(res.obj, res.token);
      return res.obj;
    } catch (err) {
      if (!(err instanceof Error && err.name === "AbortError")) {
        this.clear();
      }
      throw err;
    }
  }
  async login(params, opts) {
    const res = await this.loginApi(params, opts);
    this.setCache(res.obj, res.token);
    return res.obj;
  }
  logout() {
    this.clear();
  }
  hasTokenExpired() {
    if (typeof this.lastRefresh === "number") {
      const tokenAge = Date.now() - this.lastRefresh;
      const maxTokenAge = this.opts.authTokenTtl() + this.opts.authTokenGracePeriod;
      if (tokenAge > maxTokenAge) {
        this.clear();
        this.emit("expired");
        return true;
      }
    }
    return false;
  }
  getToken() {
    if (this.hasTokenExpired()) {
      return;
    }
    if (!this.cachedToken) {
      this.cachedToken = cookieManager.getCookie(this.opts.authCookieName()) || void 0;
    }
    return this.cachedToken;
  }
  getCache() {
    if (this.hasTokenExpired()) {
      return;
    }
    if (!this.cache) {
      return;
    }
    return structuredClone(this.cache);
  }
  /**
   * Set cached session object and auth token
   */
  setCache(cache, token) {
    this.lastRefresh = Date.now();
    this.cache = structuredClone(cache);
    this.cachedToken = token;
    cookieManager.setCookie(this.opts.authCookieName(), token);
    this.emit("authenticated", cache);
    this.stopRefreshTimers();
    this.worker.postMessage(["start", this.opts.authTokenTtl()]);
  }
  /**
   * Only used in testing context
   */
  __INTERNAL_SET_CACHE__ = (cache, token) => {
    this.cache = structuredClone(cache);
    this.cachedToken = token;
  };
  /**
   * Subscribe to event
   *
   * Returns function to unsubscribe
   *
   * @param event Event name
   * @param handler Event handler
   */
  subscribe(event, handler) {
    if (!this.eventHandlers[event].includes(handler)) {
      this.eventHandlers[event].push(handler);
    }
    return () => {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (h) => h !== handler
      );
    };
  }
};

// src/auth/util.ts
function extractAuthToken(obj) {
  return obj.authentication.dashboard;
}

// src/auth/index.ts
var AuthGatewayManager = class extends AuthManager {
  constructor() {
    super({
      authCookieName: config.select((c) => c.auth.cookies.token),
      authTokenTtl: config.select((c) => c.auth.tokenTtl)
    });
  }
  authenticateApi = PromiseUtil.coalesce(async (opts) => {
    const { jwApi } = await import('./api-MUYYEOYK.js');
    const obj = (await jwApi("dashboard-scope", "read", {}, opts)).data;
    return {
      obj,
      token: extractAuthToken(obj)
    };
  });
  /**
   * Authenticate a user with the API Gateway.
   *
   * In case MFA is set up for the user, the error `MFA_REQUIRED` is thrown when
   * the user needs to enter their MFA code.
   */
  loginApi = PromiseUtil.coalesce(async ({ email, password, mfaCode, rememberMfa }, opts) => {
    const { jwApi } = await import('./api-MUYYEOYK.js');
    email = email.trim();
    password = password.trim();
    mfaCode = mfaCode?.trim();
    const mfaSignature = cookieManager.getCookie(config.get().auth.cookies.mfa.identifier)?.trim();
    const res = await jwApi(
      "dashboard-scope",
      "create",
      {
        input: {
          email,
          password,
          // ⚠️ Don't pass empty strings
          code: mfaCode || void 0,
          remember_mfa: mfaCode ? !!rememberMfa : void 0,
          mfa_signature: mfaSignature || void 0
        }
      },
      opts
    );
    if (res.status === 204) {
      cookieManager.deleteCookie(config.get().auth.cookies.mfa.identifier);
      throw new JwAuthError("MFA_REQUIRED");
    }
    if (res.data.authentication.mfa && typeof res.data.authentication.mfa === "string") {
      cookieManager.setCookie(config.get().auth.cookies.mfa.identifier, res.data.authentication.mfa, {
        "Max-Age": config.get().auth.cookies.mfa.ttl
      });
    }
    cookieManager.deleteCookie(config.get().auth.cookies.authFromAdmin);
    return {
      obj: res.data,
      token: extractAuthToken(res.data)
    };
  });
};
var auth = new AuthGatewayManager();

export { JwAuthError, auth, getEnvVariable, isJwAuthError };
